<template>
	<v-card>
		<v-toolbar flat color="dark" dark>
			<v-toolbar-title>
				<span class="pl-2">Portal gebruikers</span>
			</v-toolbar-title>
		</v-toolbar>
		<v-card-title class="pb-0 pt-4 flex-wrap">
			<v-select
				v-model="statusFilter"
				:items="statusFilterItems"
				item-text="text"
				item-value="value"
				label="Status"
				dense
				hide-details
				class="mr-4 mb-2"
				style="max-width: 220px"
				@change="fetchUsers"
			></v-select>
			<v-select
				v-model="selected_role"
				:items="roleFilterItems"
				item-text="text"
				item-value="value"
				label="Filter op rol"
				dense
				hide-details
				class="mr-4 mb-2"
				style="max-width: 280px"
			></v-select>
			<v-text-field
				v-model="search"
				append-icon="mdi-magnify"
				label="Zoeken"
				single-line
				hide-details
				dense
				class="flex-grow-1 mb-2"
				style="min-width: 200px"
			></v-text-field>
		</v-card-title>

		<v-alert v-if="loadError" type="error" dense outlined class="mx-4 mb-0">
			{{ loadError }}
		</v-alert>

		<v-data-table
			:headers="headers"
			:items="filteredUsers"
			:search="search"
			:loading="loading"
			class="elevation-0"
		>
			<template v-slot:top>
				<v-toolbar dark dense color="primary" flat class="mb-1">
					<v-btn depressed class="ml-2" color="inverted" @click="goNew">
						<v-icon class="pr-2">mdi-account-plus</v-icon>
						Nieuwe gebruiker
					</v-btn>
				</v-toolbar>
			</template>
			<template v-slot:item.deleted="{ item }">
				<v-chip x-small :color="isUserDeleted(item) ? 'grey' : 'success'" text-color="white">
					{{ isUserDeleted(item) ? "Verwijderd" : "Actief" }}
				</v-chip>
			</template>
			<template v-slot:item.action="{ item }">
				<v-icon color="primary" style="cursor: pointer" class="mr-2" @click="goEdit(item.id)">
					mdi-pencil
				</v-icon>
			</template>
			<template v-slot:item.account_action="{ item }">
				<v-tooltip v-if="!isUserDeleted(item)" bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-icon
							v-bind="attrs"
							v-on="on"
							color="error"
							style="cursor: pointer"
							@click="openDeactivateDialog(item)"
						>
							mdi-account-off
						</v-icon>
					</template>
					<span>Gebruiker deactiveren (kan niet meer inloggen)</span>
				</v-tooltip>
				<v-tooltip v-else bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-icon
							v-bind="attrs"
							v-on="on"
							color="success"
							style="cursor: pointer"
							@click="reactivateUser(item)"
						>
							mdi-account-check
						</v-icon>
					</template>
					<span>Gebruiker weer activeren</span>
				</v-tooltip>
			</template>
			<template v-slot:item.tbl_sys_role.name="{ item }">
				{{ roleName(item) }}
			</template>
			<template v-slot:item.last_login="{ item }">
				{{ formatLastLogin(item.last_login) }}
			</template>
		</v-data-table>

		<v-dialog v-model="deactivateDialog" max-width="420">
			<v-card>
				<v-card-title>Gebruiker deactiveren?</v-card-title>
				<v-card-text>
					<p class="body-2">
						<strong>{{ deactivateTarget && deactivateTarget.username }}</strong> kan daarna niet meer inloggen
						op het portaal. U kunt de gebruiker later weer activeren via de statusfilter.
					</p>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="deactivateDialog = false">Annuleren</v-btn>
					<v-btn color="error" depressed :loading="deactivateSaving" @click="confirmDeactivate">
						Deactiveren
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
import Axios from "axios";
import { API_ROOT } from "../../config";

const ax = Axios.create({
	baseURL: API_ROOT["osbod_salary"],
	timeout: 60000
});

export default {
	name: "PortalUsers",
	data() {
		return {
			search: "",
			loading: false,
			loadError: "",
			users: [],
			roles: [],
			selected_role: 0,
			statusFilter: "active",
			statusFilterItems: [
				{ text: "Alleen actief", value: "active" },
				{ text: "Verwijderd / deactief", value: "deleted" },
				{ text: "Alle", value: "all" }
			],
			deactivateDialog: false,
			deactivateTarget: null,
			deactivateSaving: false,
			headers: [
				{ text: "Status", value: "deleted", sortable: true },
				{ text: "", align: "center", sortable: false, value: "action" },
				{ text: "", align: "center", sortable: false, value: "account_action" },
				{ text: "Rol", value: "tbl_sys_role.name" },
				{ text: "Voornaam", value: "firstname" },
				{ text: "Achternaam", value: "surname" },
				{ text: "Weergavenaam", value: "display_name" },
				{ text: "Gebruikersnaam", value: "username" },
				{ text: "Laatste login", value: "last_login" }
			]
		};
	},
	computed: {
		roleFilterItems() {
			var items = [{ text: "Alle rollen", value: 0 }];
			for (var i = 0; i < this.roles.length; i++) {
				var r = this.roles[i];
				items.push({ text: r.name, value: r.id });
			}
			return items;
		},
		filteredUsers() {
			if (!this.selected_role) return this.users;
			return this.users.filter(function(u) {
				return Number(u.role_id) === Number(this.selected_role);
			}, this);
		}
	},
	created() {
		this.fetchRoles();
		this.fetchUsers();
	},
	methods: {
		isUserDeleted(u) {
			if (!u) return false;
			var d = u.deleted;
			return d != null && Number(d) !== 0;
		},
		roleName(item) {
			return item && item.tbl_sys_role && item.tbl_sys_role.name
				? item.tbl_sys_role.name
				: "—";
		},
		formatLastLogin(value) {
			if (value == null || value === "") return "—";
			try {
				var d = value instanceof Date ? value : new Date(value);
				if (Number.isNaN(d.getTime())) return String(value);
				return d.toLocaleString("nl-NL", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit"
				});
			} catch (e) {
				return String(value);
			}
		},
		goNew() {
			this.$router.push({ path: "/schooladmin/portal_users/new" });
		},
		goEdit(id) {
			this.$router.push({ path: "/schooladmin/portal_users/update/" + id });
		},
		openDeactivateDialog(item) {
			this.deactivateTarget = item;
			this.deactivateDialog = true;
		},
		confirmDeactivate() {
			var self = this;
			if (!this.deactivateTarget || !this.deactivateTarget.id) {
				this.deactivateDialog = false;
				return;
			}
			this.deactivateSaving = true;
			ax.post("/users/update/" + this.deactivateTarget.id, { deleted: 1 })
				.then(function() {
					self.deactivateDialog = false;
					self.deactivateTarget = null;
					self.fetchUsers();
				})
				.catch(function() {
					self.loadError = "Deactiveren is mislukt.";
				})
				.finally(function() {
					self.deactivateSaving = false;
				});
		},
		reactivateUser(item) {
			var self = this;
			if (!item || !item.id) return;
			this.loading = true;
			ax.post("/users/update/" + item.id, { deleted: null })
				.then(function() {
					self.fetchUsers();
				})
				.catch(function() {
					self.loadError = "Activeren is mislukt.";
				})
				.finally(function() {
					self.loading = false;
				});
		},
		fetchRoles() {
			var self = this;
			ax.get("/role/all")
				.then(function(res) {
					self.roles = Array.isArray(res.data) ? res.data : [];
				})
				.catch(function() {
					self.roles = [];
				});
		},
		fetchUsers() {
			var self = this;
			self.loading = true;
			self.loadError = "";
			ax.get("/users/all", { params: { status: self.statusFilter } })
				.then(function(res) {
					self.users = Array.isArray(res.data) ? res.data : [];
				})
				.catch(function() {
					self.loadError =
						"Gebruikerslijst kon niet worden geladen. Controleer of u bent ingelogd en de API bereikbaar is.";
					self.users = [];
				})
				.finally(function() {
					self.loading = false;
				});
		}
	}
};
</script>
